import React, { use, useCallback, useEffect, useMemo, useState } from "react";
import mime from "mime";
import { v4 as uuidv4 } from "uuid";
import { Buffer } from "buffer";
import { FirebaseManagerContext, TEST_USER_ID } from "../contexts/FirebaseManager";
import { getStorage, ref, putFile, uploadBytes, getDownloadURL } from "../contexts/FirebaseStorage";
import type { SettableMetadata } from "../contexts/FirebaseStorage";
import { DocumentType } from "../__generated__/graphql";
import type { DisplayableDocument } from "../contexts/DocumentManager";
import * as ImagePicker from "expo-image-picker";
import * as DocumentPicker from "expo-document-picker";
import { Linking, Platform } from "react-native";
import { DocumentPickerAsset } from "expo-document-picker";
import { ImagePickerAsset } from "expo-image-picker";
import { XOR } from "ts-essentials";
import { useI18n } from "./i18n-hooks";
import { useSnackbar } from "./snackbar";
import { useWebImagePicker } from "../components/webimages/WebImagePicker";
import { useActionSheetOrDropdown } from "./use-action-sheet-or-dropdown";
import { UnifiedImagePickerAsset, useCropZoomImage } from "./crop-zoom-image";
import { useSetAlert } from "./use-set-alert";
import { DeviceType, deviceType } from "expo-device";

const IMAGE_MIMETYPE = "image/";

export enum PickerOptionsMode {
    Camera = "camera",
    Library = "library",
    Storage = "storage",
    Web = "webImage",
}

const isDesktop = deviceType === DeviceType.DESKTOP;

type UseDocumentsUploadType = {
    documentType: DocumentType;
    generateUniqueId?: boolean;
    isPublic?: boolean;
    setIsLoading?: (isLoading: boolean) => void;
    onSelect: (document: DisplayableDocument) => void | Promise<void>;
    modes?: PickerOptionsMode[];
    webUploadFn?: string;
    fixedTitle?: string;
    enableCropZoom?: boolean;
    storageTypes?: string[];
};

const sanitizeAssetBase64 = (asset: UnifiedImagePickerAsset) => {
    if (!asset.base64 || asset.base64.startsWith("data:")) {
        return asset;
    }

    return {
        ...asset,
        base64: `data:${asset.mimeType};base64,${asset.base64}`,
    };
};

export const useDocumentUpload = ({
    documentType,
    generateUniqueId = false,
    isPublic = false,
    setIsLoading,
    onSelect,
    modes = [PickerOptionsMode.Camera, PickerOptionsMode.Library],
    webUploadFn,
    fixedTitle,
    enableCropZoom,
    storageTypes,
}: UseDocumentsUploadType) => {
    const [cameraStatus, requestCameraPermission] = ImagePicker.useCameraPermissions();
    const [libraryStatus, requestLibraryPermission] = ImagePicker.useMediaLibraryPermissions();
    const { uploadDocument, controller } = useUploadDocument();
    const { showSnackbar } = useSnackbar();
    const i18n = useI18n();
    const { setAlert } = useSetAlert();
    const pickWebImage = useWebImagePicker();
    const performCropZoom = useCropZoomImage();

    // Only necessary for web & when crop is disabled
    const includeBase64 = useMemo(() => Platform.OS === "web" && !enableCropZoom, [enableCropZoom]);

    const filteredModes = useMemo(
        () =>
            modes
                // 1. On Desktop devices, replace Library with Storage
                .map((mode) =>
                    isDesktop &&
                    mode === PickerOptionsMode.Library &&
                    !modes.includes(PickerOptionsMode.Storage)
                        ? PickerOptionsMode.Storage
                        : mode,
                )
                .filter((mode) => {
                    if (mode === PickerOptionsMode.Web) {
                        // WebPicker does not works on webapp (CORS issues)
                        return Platform.OS !== "web";
                    } else if (mode === PickerOptionsMode.Library) {
                        // Remove Library on desktop devices (not relevent)
                        return !isDesktop;
                    }
                    return true;
                })
                .sort((a, b) => a.localeCompare(b)),
        [modes],
    );

    const ensureCameraPermission = React.useCallback(async () => {
        let granted = true;
        if (!cameraStatus?.granted) {
            ({ granted } = await requestCameraPermission());
        }
        return granted;
    }, [cameraStatus, requestCameraPermission]);

    const cropZoomImage = useCallback(
        async (asset: UnifiedImagePickerAsset) => {
            if (enableCropZoom && asset.mimeType?.startsWith(IMAGE_MIMETYPE)) {
                return await performCropZoom(asset);
            }
            return sanitizeAssetBase64(asset);
        },
        [enableCropZoom, performCropZoom],
    );

    const ensureLibraryPermission = React.useCallback(async () => {
        let granted = true;
        if (!libraryStatus?.granted) {
            ({ granted } = await requestLibraryPermission());
        }
        return granted;
    }, [libraryStatus, requestLibraryPermission]);

    const prepareUploadDocument = useCallback(
        async (asset: UnifiedImagePickerAsset) => {
            const newDocumentId = uuidv4();

            const baseDocument = {
                title: fixedTitle || asset.name || asset.fileName || "",
                id: newDocumentId,
                type: documentType,
                documentUrl: null,
                inlineContent: null,
            };

            await onSelect(baseDocument);

            const uploadedDocumentUrl = await uploadDocument(
                asset,
                documentType,
                isPublic,
                generateUniqueId ? newDocumentId : undefined,
            );
            if (uploadedDocumentUrl) {
                await onSelect({
                    ...baseDocument,
                    documentUrl: uploadedDocumentUrl,
                });
            } else {
                throw new Error("Error uploading document");
            }
        },
        [documentType, isPublic, onSelect, uploadDocument, generateUniqueId, fixedTitle],
    );

    const onError = useCallback(
        (isInitialUndetermined: boolean, pickerMode: PickerOptionsMode) => {
            if (isInitialUndetermined) {
                showSnackbar({
                    text: i18n.t("permission_denied"),
                    mode: "error",
                });
            } else {
                setAlert({
                    title: i18n.t(`documents.${pickerMode}_permission_previously_denied`),
                    message: i18n.t(`documents.enable_permission_in_settings`),
                    buttons: [
                        {
                            text: i18n.t("documents.settings"),
                            isPreferred: true,
                            style: "default",
                            onPress: () => {
                                void Linking.openSettings();
                            },
                        },
                        {
                            text: i18n.t("close"),
                            style: "cancel",
                        },
                    ],
                });
            }
        },
        [i18n, setAlert, showSnackbar],
    );

    const onCamera = useCallback(async () => {
        const isInitialUndetermined =
            cameraStatus?.status === ImagePicker.PermissionStatus.UNDETERMINED;
        if (await ensureCameraPermission()) {
            const pickerResult = await ImagePicker.launchCameraAsync({
                mediaTypes: "images" as ImagePicker.MediaType,
                quality: 0.6,
                base64: includeBase64,
            });
            if (pickerResult?.assets) {
                const newAsset = await cropZoomImage(pickerResult.assets[0]);
                if (newAsset) {
                    await prepareUploadDocument(newAsset);
                }
            }
        } else {
            onError(isInitialUndetermined, PickerOptionsMode.Camera);
        }
    }, [
        cameraStatus?.status,
        cropZoomImage,
        ensureCameraPermission,
        includeBase64,
        onError,
        prepareUploadDocument,
    ]);

    const onLibrary = useCallback(async () => {
        const isInitialUndetermined =
            libraryStatus?.status === ImagePicker.PermissionStatus.UNDETERMINED;
        if (await ensureLibraryPermission()) {
            const pickerResult = await ImagePicker.launchImageLibraryAsync({
                mediaTypes: "images" as ImagePicker.MediaType,
                base64: includeBase64,
            });
            if (pickerResult?.assets) {
                const newAsset = await cropZoomImage(pickerResult.assets[0]);
                if (newAsset) {
                    await prepareUploadDocument(newAsset);
                }
            }
        } else {
            onError(isInitialUndetermined, PickerOptionsMode.Library);
        }
    }, [
        cropZoomImage,
        ensureLibraryPermission,
        includeBase64,
        libraryStatus?.status,
        onError,
        prepareUploadDocument,
    ]);

    const onStorage = useCallback(async () => {
        const acceptedTypes = storageTypes ?? ["application/pdf", `${IMAGE_MIMETYPE}*`];
        const pickerResult = await DocumentPicker.getDocumentAsync({
            type: acceptedTypes,
            base64: includeBase64,
        });

        if (pickerResult?.assets) {
            const asset = pickerResult.assets[0];
            if (
                storageTypes &&
                asset.mimeType &&
                !storageTypes.some((t) =>
                    t.endsWith("*")
                        ? asset.mimeType!.startsWith(t.slice(0, -1))
                        : asset.mimeType === t,
                )
            ) {
                showSnackbar({ text: i18n.t("documents.invalid_file_type"), mode: "error" });
                return;
            }
            const newAsset = await cropZoomImage(asset);
            if (newAsset) {
                await prepareUploadDocument(newAsset);
            }
        }
    }, [cropZoomImage, i18n, includeBase64, prepareUploadDocument, showSnackbar, storageTypes]);

    const onWeb = useCallback(async () => {
        const pickerResult = await pickWebImage();

        if (pickerResult?.assets) {
            const newAsset = await cropZoomImage(pickerResult.assets[0]);
            if (newAsset) {
                await prepareUploadDocument(newAsset);
            }
        }
    }, [cropZoomImage, pickWebImage, prepareUploadDocument]);

    const onSelectOption = useCallback(
        async (selectedIndex = 0) => {
            try {
                setIsLoading?.(true);
                switch (filteredModes[selectedIndex]) {
                    case PickerOptionsMode.Camera:
                        await onCamera();
                        break;
                    case PickerOptionsMode.Library:
                        await onLibrary();
                        break;
                    case PickerOptionsMode.Storage:
                        await onStorage();
                        break;
                    case PickerOptionsMode.Web:
                        await onWeb();
                        break;
                }
            } catch (e) {
                console.warn(e);
                showSnackbar({
                    text: i18n.t("documents.select_error"),
                    mode: "error",
                });
            } finally {
                setIsLoading?.(false);
            }
        },
        [filteredModes, i18n, onCamera, onLibrary, onStorage, onWeb, setIsLoading, showSnackbar],
    );

    const dropdownItems = useMemo(
        () =>
            filteredModes.map((v, index) => ({
                label: i18n.t(v),
                onClick: () => onSelectOption(index),
                icon:
                    v === PickerOptionsMode.Camera
                        ? ("camera" as const)
                        : v === PickerOptionsMode.Library
                          ? ("images" as const)
                          : v === PickerOptionsMode.Storage
                            ? ("folder" as const)
                            : v === PickerOptionsMode.Web
                              ? ("globe" as const)
                              : undefined,
            })),
        [filteredModes, i18n, onSelectOption],
    );

    const documentDropdownProps = useActionSheetOrDropdown({
        items: dropdownItems,
        position: "right",
        setIsLoading,
    });

    useEffect(() => {
        if (Platform.OS === "web" && webUploadFn) {
            const fnName = `${webUploadFn}Upload`;
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            window[fnName] = prepareUploadDocument;

            return () => {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                window[fnName] = null;
            };
        }
    }, [prepareUploadDocument, webUploadFn]);

    return { documentDropdownProps, controller };
};

export function useUploadDocument() {
    const { user: firebaseUser } = use(FirebaseManagerContext);
    const [uploadDocumentController, setUploadDocumentController] = useState<AbortController>();

    const uploadDocument = React.useCallback(
        async (
            asset: Pick<XOR<DocumentPickerAsset, ImagePickerAsset>, "mimeType" | "uri" | "base64">,
            documentType: DocumentType,
            isPublic?: boolean,
            prefix?: string,
        ) => {
            if (firebaseUser) {
                const mimeType = asset.mimeType || "image/png";
                const ext = mime.getExtension(mimeType);
                let filePath;
                if (prefix) {
                    filePath = `/users/${firebaseUser.uid}/${prefix}_${documentType}.${ext}`;
                } else {
                    filePath = `/users/${firebaseUser.uid}/${documentType}.${ext}`;
                }
                const abortController = new AbortController();
                setUploadDocumentController(abortController);
                return uploadDocumentAsync(
                    asset.base64 || asset.uri,
                    mimeType,
                    filePath,
                    firebaseUser.uid,
                    abortController.signal,
                    isPublic,
                );
            } else {
                throw new Error("No firebase user id");
            }
        },
        [firebaseUser],
    );

    return { uploadDocument, controller: uploadDocumentController };
}

export const getDocumentUrl = async (filePath: string) => {
    if (!filePath) {
        return null;
    }
    if (filePath.startsWith("http")) {
        return filePath;
    }
    const fileRef = ref(getStorage(), filePath);
    try {
        return await getDownloadURL(fileRef);
    } catch (e) {
        // eslint-disable-next-line no-console
        console.info(e);
        // exception just mean file does not exist we can just return null
        return null;
    }
};

const uploadDocumentAsync = async (
    sourceUri: string,
    mimeType: string,
    destinationFilePath: string,
    firebaseUserId: string,
    uploadDocumentSignal: AbortSignal,
    isPublic?: boolean,
) => {
    const fileRef = ref(getStorage(), destinationFilePath);
    let metadata: SettableMetadata = {
        contentType: mimeType,
    };

    if (!firebaseUserId) {
        throw new Error("No firebase user id");
    }
    if (firebaseUserId === TEST_USER_ID) {
        return `https://__testing__.com/${destinationFilePath}`;
    }
    if (isPublic) {
        metadata = {
            ...metadata,
            customMetadata: {
                owner: "public",
            },
            cacheControl: "public,max-age: 3600",
        };
    } else {
        metadata = {
            ...metadata,
            customMetadata: {
                owner: firebaseUserId,
            },
        };
    }

    let uploadDocumentTask: ReturnType<typeof putFile>;

    if (Platform.OS !== "web") {
        uploadDocumentTask = putFile(fileRef, sourceUri, metadata);
        uploadDocumentSignal.addEventListener("abort", () => {
            void uploadDocumentTask.cancel();
        });
        await uploadDocumentTask;
    } else {
        const base64 = sourceUri.split("base64,")[1];
        await uploadBytes(fileRef, Buffer.from(base64, "base64"), metadata);
    }

    return await getDownloadURL(fileRef);
};
